import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Spin,
  Alert,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Divider,
  Badge,
  Drawer,
  List,
} from 'antd';
import {
  CarOutlined,
  StopOutlined,
  CloseCircleOutlined,
  GiftOutlined,
  EditOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UnlockOutlined,
  LockOutlined,
  UserOutlined,
  SearchOutlined,
  BellOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { formatCurrency, DEFAULT_CURRENCY } from '../utils/currency';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { GET_ALL_LOCATIONS, GET_ACTIVE_SESSIONS_FOR_PARKING, GET_SLOTS_TELEMETRY, GET_ALL_ACTIVE_SESSIONS, GET_ALL_SLOTS_TELEMETRY, SEARCH_USER_BY_PHONE_OR_EMAIL, GET_PENDING_PAYMENT_SESSIONS } from '../graphql/queries';
import { useAuth } from '../hooks/useAuth';
import {
  CREATE_STAFF_SESSION,
  END_STAFF_SESSION,
  CANCEL_STAFF_SESSION,
  UPDATE_SESSION_DISCOUNT,
  UPDATE_SESSION_PLATE,
  UPDATE_SESSION_NOTES,
  SEND_DEVICE_COMMAND,
  CONFIRM_PAYMENT_COLLECTED,
  MARK_PAYMENT_NOT_COLLECTED,
} from '../graphql/mutations';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Barrier status from telemetry
type BarrierStatus = 'UP' | 'DOWN' | 'UNKNOWN';

// Telemetry data type
interface SlotTelemetry {
  slotId: string;
  deviceId: number | null;
  barrierStatus: BarrierStatus;
  vehicleDetected: boolean;
  hasFault: boolean;
  faults: string[];
  batteryVoltage: number | null;
  lastUpdated: string | null;
}

// Slot status types (extended with telemetry states)
type SlotStatus = 'AVAILABLE' | 'RESERVED' | 'OCCUPIED' | 'DISABLED' | 'ERROR' | 'UNAUTHORIZED' | 'BARRIER_DOWN';

// Status colors for slots
const SLOT_STATUS_STYLES: Record<SlotStatus, { bg: string; border: string; text: string }> = {
  AVAILABLE: { bg: '#f6ffed', border: '#b7eb8f', text: '#52c41a' },     // Green - ready for session
  RESERVED: { bg: '#fff7e6', border: '#ffd591', text: '#fa8c16' },      // Orange - customer arriving
  OCCUPIED: { bg: '#fff1f0', border: '#ffa39e', text: '#ff4d4f' },      // Red - car on slot
  DISABLED: { bg: '#f5f5f5', border: '#d9d9d9', text: '#bfbfbf' },      // Gray - disabled
  ERROR: { bg: '#fff1f0', border: '#ff4d4f', text: '#ff4d4f' },          // Red - fault detected
  UNAUTHORIZED: { bg: '#f9f0ff', border: '#d3adf7', text: '#722ed1' },  // Purple - car without session
  BARRIER_DOWN: { bg: '#e6f7ff', border: '#91d5ff', text: '#1890ff' },  // Blue - barrier open, no session
};

// CSS keyframes for flashing animation (injected once)
const flashingStyleId = 'unauthorized-flash-style';
if (typeof document !== 'undefined' && !document.getElementById(flashingStyleId)) {
  const style = document.createElement('style');
  style.id = flashingStyleId;
  style.textContent = `
    @keyframes unauthorizedFlash {
      0%, 100% {
        background-color: #f9f0ff;
        border-color: #d3adf7;
        box-shadow: 0 0 0 0 rgba(114, 46, 209, 0.4);
      }
      50% {
        background-color: #efdbff;
        border-color: #722ed1;
        box-shadow: 0 0 12px 4px rgba(114, 46, 209, 0.6);
      }
    }
    .slot-unauthorized {
      animation: unauthorizedFlash 1s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

// CSS keyframes for resetting barrier flash (yellow)
const resettingStyleId = 'resetting-flash-style';
if (typeof document !== 'undefined' && !document.getElementById(resettingStyleId)) {
  const style = document.createElement('style');
  style.id = resettingStyleId;
  style.textContent = `
    @keyframes resettingFlash {
      0%, 100% {
        background-color: #fffbe6;
        border-color: #ffe58f;
        box-shadow: 0 0 0 0 rgba(250, 173, 20, 0.4);
      }
      50% {
        background-color: #fff1b8;
        border-color: #faad14;
        box-shadow: 0 0 12px 4px rgba(250, 173, 20, 0.6);
      }
    }
    .slot-resetting {
      animation: resettingFlash 1.5s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

// Session type options
const SESSION_TYPES = [
  { value: 'WALK_IN', label: 'Walk-in', color: 'purple' },
  { value: 'STAFF', label: 'Staff', color: 'blue' },
  { value: 'VIP', label: 'VIP', color: 'magenta' },
  { value: 'MAINTENANCE', label: 'Maintenance', color: 'orange' },
];

// Billing type options
const BILLING_TYPES = [
  { value: 'STANDARD', label: 'Standard', color: 'blue', description: 'Auto-charge if user assigned' },
  { value: 'FREE', label: 'Free', color: 'green', description: 'No charge' },
  { value: 'MANUAL', label: 'Manual', color: 'orange', description: 'Staff collects payment' },
];

// Format duration from start time
const formatDuration = (startTime: string | null): string => {
  if (!startTime) return '--';
  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) return `${diffMins}m`;
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

// Get fee from session - prefers server-calculated fees, falls back to client estimate
// Server fees use actual pricing templates; client estimate uses default rate for preview only
const DEFAULT_HOURLY_RATE_FALLBACK = 5; // Fallback for real-time preview only (matches fee-config.ts default)

const getSessionFee = (session: any): { calculatedFee: number; finalFee: number; durationMinutes: number } => {
  if (!session) return { calculatedFee: 0, finalFee: 0, durationMinutes: 0 };

  // FREE billing = $0
  if (session.billingType === 'FREE') {
    const durationMinutes = session.durationMinutes ?? calculateDurationMinutes(session.startedAt || session.reservedAt);
    return { calculatedFee: 0, finalFee: 0, durationMinutes };
  }

  // Use server-calculated fees if available (these use actual pricing templates)
  if (session.calculatedFee !== null && session.calculatedFee !== undefined) {
    return {
      calculatedFee: Number(session.calculatedFee) || 0,
      finalFee: Number(session.finalFee) ?? Number(session.calculatedFee) ?? 0,
      durationMinutes: session.durationMinutes ?? calculateDurationMinutes(session.startedAt || session.reservedAt),
    };
  }

  // Fall back to client-side estimate for real-time preview (active sessions)
  return calculateClientFeeEstimate(
    session.startedAt || session.reservedAt,
    session.discountPercent
  );
};

// Calculate duration in minutes from start time
const calculateDurationMinutes = (startTime: string | null): number => {
  if (!startTime) return 0;
  const start = new Date(startTime);
  return Math.ceil((Date.now() - start.getTime()) / 1000 / 60);
};

// Client-side fee estimate (fallback for real-time preview only)
// NOTE: This uses a simplified calculation and doesn't reflect actual pricing templates
const calculateClientFeeEstimate = (
  startTime: string | null,
  discountPercent?: number | null
): { calculatedFee: number; finalFee: number; durationMinutes: number } => {
  if (!startTime) return { calculatedFee: 0, finalFee: 0, durationMinutes: 0 };

  const durationMinutes = calculateDurationMinutes(startTime);

  // Simple estimate: 15-minute intervals at fallback rate
  const intervals = Math.ceil(durationMinutes / 15);
  const ratePerInterval = DEFAULT_HOURLY_RATE_FALLBACK / 4;
  let calculatedFee = intervals * ratePerInterval;

  // Minimum charge $1
  if (calculatedFee > 0) {
    calculatedFee = Math.max(calculatedFee, 1);
  }
  calculatedFee = Math.round(calculatedFee * 100) / 100;

  // Apply discount
  const discount = discountPercent || 0;
  let finalFee = calculatedFee;
  if (discount > 0) {
    finalFee = calculatedFee * (1 - discount / 100);
    finalFee = Math.round(finalFee * 100) / 100;
  }

  return { calculatedFee, finalFee, durationMinutes };
};

// Format fee for display with currency
const formatFee = (fee: number, currency?: string): string => {
  return formatCurrency(fee, currency || DEFAULT_CURRENCY);
};

// Check if car is likely present based on session status
const isCarPresent = (session: any): boolean => {
  if (!session) return false;
  // ACTIVE or LEAVING means car is on the slot
  return ['ACTIVE', 'LEAVING'].includes(session.status);
};

// Special value for "All Parkings"
const ALL_PARKINGS = 'all';

export const Operations: React.FC = () => {
  // Default to 'all' to show all parkings
  const [filterParkingId, setFilterParkingId] = useState<string>(ALL_PARKINGS);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isSessionDetailsVisible, setIsSessionDetailsVisible] = useState(false);
  const [isEndSessionModalVisible, setIsEndSessionModalVisible] = useState(false);
  const [pendingEndSession, setPendingEndSession] = useState<{ raiseBarrier: boolean } | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // User assignment state for create session
  const [assignedUser, setAssignedUser] = useState<any>(null);
  const [userSearchValue, setUserSearchValue] = useState('');

  // Pending payments drawer state
  const [isPendingPaymentsVisible, setIsPendingPaymentsVisible] = useState(false);

  // Fixed cost for session details (charge exact amount)
  const [fixedCost, setFixedCost] = useState<number | null>(null);

  // FAULT modal state
  const [isFaultModalVisible, setIsFaultModalVisible] = useState(false);
  const [faultModalStep, setFaultModalStep] = useState<'initial' | 'confirm' | 'success'>('initial');

  // Track slots that are currently resetting (for yellow flash)
  const [resettingSlots, setResettingSlots] = useState<Record<string, number>>({});

  // Get auth context for org access
  const { memberships } = useAuth();

  // Get all locations (filtered by role in resolver)
  const { data: locationsData, loading: locationsLoading } = useQuery(GET_ALL_LOCATIONS);

  // Get all parkings
  const allLocations = locationsData?.getAllLocations || [];
  const parkings = useMemo(() =>
    allLocations.filter((l: any) => l.type === 'PARKING'),
    [allLocations]
  );

  // Group parkings by organization for the dropdown
  const parkingsByOrg = useMemo(() => {
    const grouped: Record<string, { orgName: string; parkings: any[] }> = {};
    parkings.forEach((parking: any) => {
      const orgName = parking.organization?.name || 'No Organization';
      const orgId = parking.organization?.id || 'none';
      if (!grouped[orgId]) {
        grouped[orgId] = { orgName, parkings: [] };
      }
      grouped[orgId].parkings.push(parking);
    });
    return grouped;
  }, [parkings]);

  // Determine active parking ID (null when showing all)
  const isShowingAll = filterParkingId === ALL_PARKINGS;
  const activeParkingId = isShowingAll ? null : filterParkingId;

  // Query sessions for a specific parking (single parking mode)
  const {
    data: sessionsData,
    loading: sessionsLoading,
    refetch: refetchSessions,
  } = useQuery(GET_ACTIVE_SESSIONS_FOR_PARKING, {
    variables: { parkingId: activeParkingId },
    skip: !activeParkingId, // Skip when showing all
    pollInterval: 5000,
  });

  // Query telemetry for a specific parking (single parking mode)
  const {
    data: telemetryData,
    refetch: refetchTelemetry,
  } = useQuery(GET_SLOTS_TELEMETRY, {
    variables: { parkingId: activeParkingId },
    skip: !activeParkingId, // Skip when showing all
    pollInterval: 5000,
  });

  // Query ALL sessions across all parkings (overview mode)
  const {
    data: allSessionsData,
    loading: allSessionsLoading,
    refetch: refetchAllSessionsQuery,
  } = useQuery(GET_ALL_ACTIVE_SESSIONS, {
    skip: !isShowingAll, // Only query when showing all
    pollInterval: 5000,
  });

  // Query ALL telemetry across all parkings (overview mode)
  const {
    data: allTelemetryData,
    refetch: refetchAllTelemetryQuery,
  } = useQuery(GET_ALL_SLOTS_TELEMETRY, {
    skip: !isShowingAll, // Only query when showing all
    pollInterval: 5000,
  });

  // Query pending payment sessions (MANUAL billing that need staff confirmation)
  const {
    data: pendingPaymentsData,
    loading: pendingPaymentsLoading,
    refetch: refetchPendingPayments,
  } = useQuery(GET_PENDING_PAYMENT_SESSIONS, {
    variables: { hoursBack: 2 },
    pollInterval: 10000, // Poll every 10 seconds
  });

  const pendingPaymentSessions = pendingPaymentsData?.getPendingPaymentSessions || [];

  // Merge sessions from either single parking or all parkings query
  const allSessions = isShowingAll
    ? (allSessionsData?.getAllActiveSessions || [])
    : (sessionsData?.getActiveSessionsForParking || []);

  // Merge telemetry from either single parking or all parkings query
  const slotTelemetryList: SlotTelemetry[] = isShowingAll
    ? (allTelemetryData?.getAllSlotsTelemetry || [])
    : (telemetryData?.getSlotsTelemetry || []);

  // Create telemetry lookup map
  const telemetryBySlotId = useMemo(() => {
    const map: Record<string, SlotTelemetry> = {};
    slotTelemetryList.forEach((t: SlotTelemetry) => {
      map[t.slotId] = t;
    });
    return map;
  }, [slotTelemetryList]);

  const refetchAllSessions = () => {
    if (isShowingAll) {
      refetchAllSessionsQuery();
      refetchAllTelemetryQuery();
    } else {
      refetchSessions();
      refetchTelemetry();
    }
  };

  // Lazy query for user search
  const [searchUser, { data: searchUserData, loading: searchingUser }] = useLazyQuery(
    SEARCH_USER_BY_PHONE_OR_EMAIL,
    { fetchPolicy: 'network-only' }
  );

  // Mutations
  const [createStaffSession, { loading: creating }] = useMutation(CREATE_STAFF_SESSION);
  const [endStaffSession, { loading: ending }] = useMutation(END_STAFF_SESSION);
  const [cancelStaffSession] = useMutation(CANCEL_STAFF_SESSION);
  const [updateSessionDiscount] = useMutation(UPDATE_SESSION_DISCOUNT);
  const [updateSessionPlate] = useMutation(UPDATE_SESSION_PLATE);
  const [updateSessionNotes] = useMutation(UPDATE_SESSION_NOTES);
  const [sendDeviceCommand, { loading: sendingCommand }] = useMutation(SEND_DEVICE_COMMAND);
  const [confirmPaymentCollected, { loading: confirmingPayment }] = useMutation(CONFIRM_PAYMENT_COLLECTED);
  const [markPaymentNotCollected, { loading: markingUnpaid }] = useMutation(MARK_PAYMENT_NOT_COLLECTED);

  // Build hierarchy and session map
  const { slotsForParking, sessionsBySlotId } = useMemo(() => {
    const sectorsList = allLocations.filter((l: any) => l.type === 'SECTOR');
    const slotsList = allLocations.filter((l: any) => l.type === 'SLOT');

    const slotsMap: Record<string, any[]> = {};

    parkings.forEach((parking: any) => {
      const parkingSectors = sectorsList.filter((s: any) => s.parent?.id === parking.id);
      const sectorIds = parkingSectors.map((s: any) => s.id);
      const parkingSlots = slotsList.filter((slot: any) =>
        slot.parent && sectorIds.includes(slot.parent.id)
      );
      slotsMap[parking.id] = parkingSlots.map((slot: any) => ({
        ...slot,
        sectorName: parkingSectors.find((s: any) => s.id === slot.parent?.id)?.name || 'Unknown',
        parkingName: parking.name,
        parkingId: parking.id,
      }));
    });

    const sessionsMap: Record<string, any> = {};
    allSessions.forEach((session: any) => {
      if (session.location?.id) {
        sessionsMap[session.location.id] = session;
      }
    });

    return { slotsForParking: slotsMap, sessionsBySlotId: sessionsMap };
  }, [allLocations, parkings, allSessions]);

  // Sync selected session with polled data - handle auto-ended sessions
  useEffect(() => {
    if (!selectedSlot?.id) return;

    const freshSession = sessionsBySlotId[selectedSlot.id];

    if (selectedSession && !freshSession) {
      // Session ended while modal was open (auto-end)
      // Check if this session requires manual payment collection
      const needsPaymentCollection = requiresManualCollection(selectedSession);

      if (needsPaymentCollection && (isSessionDetailsVisible || isEndSessionModalVisible)) {
        // Session auto-ended but requires manual payment collection
        // Show Payment Collection Modal so staff can confirm payment
        message.warning('Session auto-ended - please confirm payment collection');
        setIsSessionDetailsVisible(false);
        setIsEndSessionModalVisible(true);
        setPendingEndSession({ raiseBarrier: false }); // Barrier already raised by auto-end
        // Keep selectedSession data for the Payment Modal to display
        return;
      }

      // Normal auto-end (no manual collection needed) - close everything
      if (isSessionDetailsVisible || isEndSessionModalVisible) {
        message.info('Session has ended');
        setIsSessionDetailsVisible(false);
        setIsEndSessionModalVisible(false);
        setPendingEndSession(null);
      }
      setSelectedSession(null);
      setSelectedSlot(null);
    } else if (freshSession && selectedSession) {
      // Keep selectedSession data fresh (status, fees, etc. may have changed)
      setSelectedSession(freshSession);
    }
  }, [sessionsBySlotId, selectedSlot?.id]);

  // Reset selected session when changing parking filter
  useEffect(() => {
    setSelectedSession(null);
    setSelectedSlot(null);
    setIsSessionDetailsVisible(false);
    setIsEndSessionModalVisible(false);
    setPendingEndSession(null);
  }, [filterParkingId]);

  // Clear resetting state when fault clears from telemetry
  useEffect(() => {
    Object.keys(resettingSlots).forEach(slotId => {
      const telemetry = telemetryBySlotId[slotId];
      if (telemetry && !telemetry.hasFault) {
        setResettingSlots(prev => {
          const updated = { ...prev };
          delete updated[slotId];
          return updated;
        });
        message.success('Barrier reset successful');
      }
    });
  }, [telemetryBySlotId, resettingSlots]);

  // Show all parkings or just the selected one
  const displayParkings = isShowingAll
    ? parkings
    : parkings.filter((p: any) => p.id === activeParkingId);

  // Detect unauthorized slots for warning
  const unauthorizedSlots = useMemo(() => {
    const unauthorized: { slotName: string; slotCode: string; sectorName: string }[] = [];
    displayParkings.forEach((parking: any) => {
      const slots = slotsForParking[parking.id] || [];
      slots.forEach((slot: any) => {
        if (!slot.enabled) return;
        const session = sessionsBySlotId[slot.id];
        const telemetry = telemetryBySlotId[slot.id];
        // Unauthorized: no session but vehicle detected
        if (!session && telemetry?.vehicleDetected) {
          unauthorized.push({
            slotName: slot.name,
            slotCode: slot.code,
            sectorName: slot.sectorName || 'Unknown',
          });
        }
      });
    });
    return unauthorized;
  }, [displayParkings, slotsForParking, sessionsBySlotId, telemetryBySlotId]);

  // Get slot status (using both session and telemetry)
  const getSlotStatus = (slot: any): SlotStatus => {
    if (!slot.enabled) return 'DISABLED';

    const session = sessionsBySlotId[slot.id];
    const telemetry = telemetryBySlotId[slot.id];

    // Check for faults first
    if (telemetry?.hasFault) return 'ERROR';

    // If there's an active session
    if (session) {
      if (session.status === 'RESERVED' || session.status === 'APPROACHING') return 'RESERVED';
      return 'OCCUPIED';
    }

    // No session - check telemetry for anomalies
    if (telemetry) {
      // Car detected without session = unauthorized
      if (telemetry.vehicleDetected) return 'UNAUTHORIZED';
      // Barrier down without session (could be maintenance or stuck)
      if (telemetry.barrierStatus === 'DOWN') return 'BARRIER_DOWN';
    }

    // Available: no session, barrier up (or unknown), no car
    return 'AVAILABLE';
  };

  // Barrier control with safety checks
  const handleBarrierCommand = async (
    deviceId: number,
    command: 'up' | 'down',
    slotName?: string,
    skipMessage?: boolean
  ) => {
    try {
      const result = await sendDeviceCommand({
        variables: { deviceId, command },
      });

      if (result.data?.sendDeviceCommand?.success) {
        if (!skipMessage) {
          message.success(`Barrier ${command === 'down' ? 'lowered' : 'raised'}${slotName ? ` for ${slotName}` : ''}`);
        }
        return true;
      } else {
        message.error(result.data?.sendDeviceCommand?.message || 'Command failed');
        return false;
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to send barrier command');
      return false;
    }
  };

  // Handle barrier reset command for FAULT slots
  const handleResetBarrier = async (slot: any) => {
    if (!slot?.deviceId) {
      message.error('No device ID associated with this slot');
      return;
    }

    try {
      const result = await sendDeviceCommand({
        variables: { deviceId: slot.deviceId, command: 'reset' },
      });

      if (result.data?.sendDeviceCommand?.success) {
        // Track this slot as resetting (for yellow flash)
        setResettingSlots(prev => ({
          ...prev,
          [slot.id]: Date.now(),
        }));

        // Show success step
        setFaultModalStep('success');

        // Auto-clear after 60 seconds
        setTimeout(() => {
          setResettingSlots(prev => {
            const updated = { ...prev };
            delete updated[slot.id];
            return updated;
          });
        }, 60000);
      } else {
        message.error(result.data?.sendDeviceCommand?.message || 'Reset command failed');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to send reset command');
    }
  };

  // Handle confirming payment collected for pending payment sessions
  const handleConfirmPayment = async (sessionId: string) => {
    try {
      const result = await confirmPaymentCollected({
        variables: { sessionId },
      });

      if (result.data?.confirmPaymentCollected?.success) {
        message.success('Payment confirmed');
        refetchPendingPayments();
      } else {
        // Show who already collected it
        const { alreadyCollectedBy, message: errorMsg } = result.data?.confirmPaymentCollected || {};
        if (alreadyCollectedBy) {
          message.warning(`${errorMsg}`);
        } else {
          message.error(errorMsg || 'Failed to confirm payment');
        }
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to confirm payment');
    }
  };

  // Handle marking payment as not collected
  const handleMarkUnpaid = async (sessionId: string) => {
    try {
      const result = await markPaymentNotCollected({
        variables: { sessionId },
      });

      if (result.data?.markPaymentNotCollected?.success) {
        message.info('Session marked as unpaid');
        refetchPendingPayments();
      } else {
        const { alreadyCollectedBy, message: errorMsg } = result.data?.markPaymentNotCollected || {};
        if (alreadyCollectedBy) {
          message.warning(`${errorMsg}`);
        } else {
          message.error(errorMsg || 'Failed to mark as unpaid');
        }
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to mark as unpaid');
    }
  };

  // Handle slot click - direct management enabled for all modes
  const handleSlotClick = (slot: any) => {
    const status = getSlotStatus(slot);
    const session = sessionsBySlotId[slot.id];

    if (status === 'DISABLED') {
      message.warning('This slot is disabled');
      return;
    }

    // Handle FAULT/ERROR status - open fault modal
    if (status === 'ERROR') {
      setSelectedSlot(slot);
      setFaultModalStep('initial');
      setIsFaultModalVisible(true);
      return;
    }

    if (status === 'AVAILABLE') {
      // Create session modal
      setSelectedSlot(slot);
      createForm.resetFields();
      createForm.setFieldsValue({ sessionType: 'WALK_IN', billingType: 'STANDARD' });
      setIsCreateModalVisible(true);
    } else {
      // Show session details
      setSelectedSession(session);
      setSelectedSlot(slot);
      editForm.setFieldsValue({
        plateNumber: session?.plateNumber,
        notes: session?.notes,
      });
      setIsSessionDetailsVisible(true);
    }
  };

  // Create session and optionally lower barrier
  const handleCreateSession = async (values: any, lowerBarrier: boolean = false) => {
    if (!selectedSlot) return;
    try {
      await createStaffSession({
        variables: {
          input: {
            slotId: selectedSlot.id,
            sessionType: values.sessionType,
            billingType: values.billingType || 'STANDARD',
            discountPercent: values.discountPercent || null,
            lowerBarrier,
            plateNumber: values.plateNumber || null,
            notes: values.notes || null,
            onBehalfOfUserId: assignedUser?.id || null,
          },
        },
      });

      const userInfo = assignedUser ? ` (for ${assignedUser.email})` : '';
      if (lowerBarrier) {
        message.success(`Session created & barrier lowered for ${selectedSlot.name}${userInfo}`);
      } else {
        message.success(`Session created for ${selectedSlot.name}${userInfo}`);
      }

      setIsCreateModalVisible(false);
      setSelectedSlot(null);
      setAssignedUser(null);
      setUserSearchValue('');
      refetchAllSessions();
    } catch (error: any) {
      message.error(error.message || 'Failed to create session');
    }
  };

  // Check if session requires manual payment collection
  const requiresManualCollection = (session: any): boolean => {
    if (!session) return false;
    // MANUAL billing always requires staff to collect payment
    if (session.billingType === 'MANUAL') return true;
    // STANDARD billing but no user = can't auto-charge
    if (session.billingType === 'STANDARD' && !session.user) return true;
    // No billingType set (legacy) and no user = can't auto-charge
    if (!session.billingType && !session.user) return true;
    // FREE billing = no collection needed
    return false;
  };

  // End session and optionally raise barrier
  const handleEndSessionAndRaise = async (raiseBarrier: boolean = false) => {
    if (!selectedSession || !selectedSlot) {
      return;
    }

    // Safety check: don't raise if car is present
    if (raiseBarrier && isCarPresent(selectedSession)) {
      message.error('Cannot raise barrier while car is on the slot');
      return;
    }

    // Check if we need to show the payment collection modal
    if (requiresManualCollection(selectedSession)) {
      // Close Session Details Modal first, then open Payment Collection Modal
      // This prevents having two modals open at the same time
      setIsSessionDetailsVisible(false);
      setPendingEndSession({ raiseBarrier });
      setIsEndSessionModalVisible(true);
      return;
    }

    // Otherwise, proceed directly (auto-charge possible)
    await executeEndSession(raiseBarrier);
  };

  // Actually execute the end session
  const executeEndSession = async (raiseBarrier: boolean = false, paymentStatus?: 'collected' | 'unpaid') => {
    if (!selectedSession || !selectedSlot) return;

    // Check if session has already auto-ended (not in active sessions anymore)
    const sessionStillActive = sessionsBySlotId[selectedSlot.id];

    try {
      if (sessionStillActive) {
        // Session is still active - end it via mutation
        await endStaffSession({ variables: { sessionId: selectedSession.id } });

        if (raiseBarrier && selectedSlot.deviceId) {
          await handleBarrierCommand(selectedSlot.deviceId, 'up', selectedSlot.name, true);
          message.success('Session ended & barrier raised');
        } else {
          message.success('Session ended');
        }
      }

      // Show payment collection status message
      if (paymentStatus === 'collected') {
        message.success('Payment collected');
      } else if (paymentStatus === 'unpaid') {
        message.warning('Session ended - payment pending');
      }

      setIsSessionDetailsVisible(false);
      setIsEndSessionModalVisible(false);
      setPendingEndSession(null);
      setSelectedSession(null);
      setSelectedSlot(null);
      refetchAllSessions();
    } catch (error: any) {
      message.error(error.message || 'Failed to end session');
    }
  };

  // Cancel session
  const handleCancelSession = async () => {
    if (!selectedSession) return;
    try {
      await cancelStaffSession({
        variables: { sessionId: selectedSession.id, reason: 'Cancelled by staff' }
      });
      message.success('Session cancelled');
      setIsSessionDetailsVisible(false);
      refetchAllSessions();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel session');
    }
  };

  // Update discount (100% = FREE)
  const handleUpdateDiscount = async (discountPercent: number, reason?: string) => {
    if (!selectedSession) return;
    try {
      await updateSessionDiscount({
        variables: {
          sessionId: selectedSession.id,
          discountPercent,
          reason: reason || (discountPercent === 100 ? 'Marked free by staff' : `${discountPercent}% discount applied`)
        }
      });
      const label = discountPercent === 100 ? 'free' : `${discountPercent}% discount`;
      message.success(`Session marked as ${label}`);
      refetchAllSessions();
    } catch (error: any) {
      message.error(error.message || 'Failed to update discount');
    }
  };

  // Update session info
  const handleUpdateSession = async (values: any) => {
    if (!selectedSession) return;
    try {
      const updates = [];
      if (values.plateNumber !== selectedSession.plateNumber) {
        updates.push(updateSessionPlate({
          variables: { sessionId: selectedSession.id, plateNumber: values.plateNumber || '' },
        }));
      }
      if (values.notes !== selectedSession.notes) {
        updates.push(updateSessionNotes({
          variables: { sessionId: selectedSession.id, notes: values.notes || '' },
        }));
      }
      if (updates.length > 0) {
        await Promise.all(updates);
        message.success('Session updated');
        refetchAllSessions();
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to update session');
    }
  };

  // Get organization ID for user search (from first membership or selected parking)
  const organizationId = useMemo(() => {
    if (selectedSlot?.organization?.id) return selectedSlot.organization.id;
    // Find org from selected parking or first available parking
    const targetParking = activeParkingId
      ? parkings.find((p: any) => p.id === activeParkingId)
      : parkings[0];
    return targetParking?.organization?.id || memberships?.[0]?.organization?.id;
  }, [selectedSlot, activeParkingId, parkings, memberships]);

  // Handle user search
  const handleUserSearch = () => {
    if (!userSearchValue.trim() || !organizationId) return;

    const searchValue = userSearchValue.trim();
    const isEmail = searchValue.includes('@');

    searchUser({
      variables: {
        organizationId,
        email: isEmail ? searchValue : undefined,
        phone: !isEmail ? searchValue : undefined,
      },
    });
  };

  // Handle selecting found user
  const handleSelectUser = (user: any) => {
    setAssignedUser(user);
    setUserSearchValue('');
  };

  // Clear assigned user
  const handleClearUser = () => {
    setAssignedUser(null);
    setUserSearchValue('');
  };

  // Calculate stats
  const stats = useMemo(() => {
    let total = 0, available = 0, occupied = 0, reserved = 0;
    displayParkings.forEach((parking: any) => {
      const slots = slotsForParking[parking.id] || [];
      slots.forEach((slot: any) => {
        total++;
        const status = getSlotStatus(slot);
        if (status === 'AVAILABLE') available++;
        else if (status === 'OCCUPIED') occupied++;
        else if (status === 'RESERVED') reserved++;
      });
    });
    return { total, available, occupied, reserved };
  }, [displayParkings, slotsForParking, sessionsBySlotId]);

  if (locationsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (parkings.length === 0) {
    return (
      <Alert
        message="No Access"
        description="You don't have access to any parkings. Please contact your administrator."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Operations</Title>
        </Col>
        <Col>
          <Badge count={pendingPaymentSessions.length} offset={[-5, 5]}>
            <Button
              icon={<BellOutlined />}
              onClick={() => setIsPendingPaymentsVisible(true)}
              type={pendingPaymentSessions.length > 0 ? 'primary' : 'default'}
            >
              Pending Payments
            </Button>
          </Badge>
        </Col>
      </Row>

      {/* Header with parking selector and stats */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space>
              <Text strong>Parking:</Text>
              <Select
                style={{ width: 320 }}
                value={filterParkingId}
                onChange={setFilterParkingId}
                size="large"
              >
                {/* All Parkings option */}
                <Option key={ALL_PARKINGS} value={ALL_PARKINGS}>
                  <Text strong>All Parkings</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>({parkings.length})</Text>
                </Option>

                {/* Grouped by organization */}
                {Object.entries(parkingsByOrg).map(([orgId, { orgName, parkings: orgParkings }]) => (
                  <Select.OptGroup key={orgId} label={orgName}>
                    {orgParkings.map((parking: any) => (
                      <Option key={parking.id} value={parking.id}>
                        {parking.name} ({parking.code})
                      </Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
              {isShowingAll && (
                <Tag color="blue">Overview Mode</Tag>
              )}
            </Space>
          </Col>
          <Col>
            <Statistic
              title="Available"
              value={stats.available}
              suffix={`/ ${stats.total}`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col>
            <Statistic
              title="Occupied"
              value={stats.occupied}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col>
            <Statistic
              title="Reserved"
              value={stats.reserved}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={refetchAllSessions}
              loading={isShowingAll ? allSessionsLoading : sessionsLoading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>


      {/* Unauthorized Warning Alert */}
      {unauthorizedSlots.length > 0 && (
        <Alert
          type="warning"
          showIcon
          banner
          style={{ marginBottom: 16 }}
          message={
            <span style={{ fontWeight: 'bold', fontSize: 16 }}>
              ⚠️ UNAUTHORIZED VEHICLE{unauthorizedSlots.length > 1 ? 'S' : ''} DETECTED!
            </span>
          }
          description={
            <div style={{ marginTop: 8 }}>
              {unauthorizedSlots.map((slot, idx) => (
                <Tag key={idx} color="purple" style={{ marginBottom: 4, fontSize: 14 }}>
                  {slot.sectorName} → {slot.slotCode || slot.slotName}
                </Tag>
              ))}
              <div style={{ marginTop: 8, color: '#666' }}>
                Vehicle detected without active session. Please investigate immediately.
              </div>
            </div>
          }
        />
      )}

      {/* Legend */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.AVAILABLE.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.AVAILABLE.border}`
            }} />
            <Text>Available (Barrier UP)</Text>
          </Space>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.RESERVED.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.RESERVED.border}`
            }} />
            <Text>Reserved</Text>
          </Space>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.OCCUPIED.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.OCCUPIED.border}`
            }} />
            <Text>Occupied (Car)</Text>
          </Space>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.BARRIER_DOWN.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.BARRIER_DOWN.border}`
            }} />
            <Text>Barrier DOWN</Text>
          </Space>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.UNAUTHORIZED.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.UNAUTHORIZED.border}`
            }} />
            <Text>Unauthorized Car</Text>
          </Space>
          <Space>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: SLOT_STATUS_STYLES.ERROR.bg,
              border: `2px solid ${SLOT_STATUS_STYLES.ERROR.border}`
            }} />
            <Text>Device Fault</Text>
          </Space>
        </Space>
      </Card>

      {/* Parking sections with slot grids */}
      {displayParkings.map((parking: any) => {
        const slots = slotsForParking[parking.id] || [];
        if (slots.length === 0) return null;

        // Group by sector
        const slotsBySector: Record<string, any[]> = {};
        slots.forEach((slot: any) => {
          const sectorName = slot.sectorName || 'Unknown';
          if (!slotsBySector[sectorName]) slotsBySector[sectorName] = [];
          slotsBySector[sectorName].push(slot);
        });

        return (
          <Card
            key={parking.id}
            title={`${parking.name} (${parking.code})`}
            style={{ marginBottom: 16 }}
            size="small"
          >
            {Object.entries(slotsBySector).map(([sectorName, sectorSlots]) => (
              <div key={sectorName} style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>{sectorName}</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {(sectorSlots as any[])
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((slot: any) => {
                    const status = getSlotStatus(slot);
                    const session = sessionsBySlotId[slot.id];
                    const telemetry = telemetryBySlotId[slot.id];
                    const styles = SLOT_STATUS_STYLES[status];
                    const carPresent = isCarPresent(session);
                    const barrierStatus = telemetry?.barrierStatus || 'UNKNOWN';

                    return (
                      <Tooltip
                        key={slot.id}
                        title={
                          <div>
                            <div><strong>{slot.name}</strong></div>
                            {session?.user?.email && <div>User: {session.user.email}</div>}
                            {session?.user?.phone && <div>Phone: {session.user.phone}</div>}
                            {(session?.plateNumber || session?.vehicle?.plateNumber) && (
                              <div>Plate: {session.plateNumber || session.vehicle?.plateNumber}</div>
                            )}
                            <div>Barrier: {barrierStatus}</div>
                            {telemetry?.vehicleDetected && <div style={{ color: '#ff4d4f' }}>Vehicle detected</div>}
                            {telemetry?.hasFault && (
                              <div style={{ color: '#ff4d4f' }}>
                                Faults: {telemetry.faults.join(', ')}
                              </div>
                            )}
                            {status === 'UNAUTHORIZED' && <div style={{ color: '#722ed1' }}>Unauthorized vehicle!</div>}
                            {session?.createdByStaff && <div style={{ color: '#fa8c16' }}>Staff-created</div>}
                            {status === 'AVAILABLE' && <div>Click to create session</div>}
                            {status !== 'AVAILABLE' && status !== 'DISABLED' && status !== 'ERROR' && <div>Click to manage</div>}
                          </div>
                        }
                      >
                        <div
                          onClick={() => handleSlotClick(slot)}
                          className={
                            resettingSlots[slot.id]
                              ? 'slot-resetting'
                              : status === 'UNAUTHORIZED'
                                ? 'slot-unauthorized'
                                : undefined
                          }
                          style={{
                            width: 140,
                            padding: 12,
                            borderRadius: 15,
                            backgroundColor: styles.bg,
                            border: `3px solid ${styles.border}`,
                            cursor: status !== 'DISABLED' ? 'pointer' : 'not-allowed',
                            textAlign: 'center',
                            position: 'relative',
                          }}
                        >
                          {/* Slot Name */}
                          <div style={{
                            fontSize: 14,
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: 4,
                          }}>
                            {slot.name}
                          </div>

                          {/* Barrier indicator (text) */}
                          <div style={{
                            fontSize: 13,
                            fontWeight: 'bold',
                            color: barrierStatus === 'UP' ? '#52c41a' : barrierStatus === 'DOWN' ? '#1890ff' : '#bfbfbf',
                            marginBottom: 4,
                          }}>
                            {barrierStatus === 'UP' ? 'Barrier UP' : barrierStatus === 'DOWN' ? 'Barrier DOWN' : 'Barrier ?'}
                          </div>

                          {/* Status Text with small icon */}
                          <div style={{
                            fontSize: 20,
                            fontWeight: 'bold',
                            color: styles.text,
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                          }}>
                            <span style={{ fontSize: 14 }}>
                              {status === 'AVAILABLE' ? <LockOutlined /> :
                               status === 'RESERVED' ? <ClockCircleOutlined /> :
                               status === 'OCCUPIED' ? <CarOutlined /> :
                               status === 'ERROR' ? <StopOutlined /> :
                               status === 'UNAUTHORIZED' ? <CarOutlined /> :
                               status === 'BARRIER_DOWN' ? <UnlockOutlined /> :
                               status === 'DISABLED' ? <StopOutlined /> : null}
                            </span>
                            {status === 'AVAILABLE' ? 'Available' :
                             status === 'RESERVED' ? 'Reserved' :
                             status === 'OCCUPIED' ? 'Occupied' :
                             status === 'ERROR' ? 'Fault' :
                             status === 'UNAUTHORIZED' ? 'Unauthorized' :
                             status === 'BARRIER_DOWN' ? 'Open' :
                             status === 'DISABLED' ? 'Disabled' : status}
                          </div>

                          {/* Plate Number or Status Label */}
                          <div style={{
                            fontSize: 18,
                            fontWeight: 'bold',
                            color: styles.text,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {(session?.plateNumber || session?.vehicle?.plateNumber)
                              ? (session.plateNumber || session.vehicle?.plateNumber)
                              : status === 'ERROR'
                                ? 'FAULT'
                                : status === 'UNAUTHORIZED'
                                  ? 'UNAUTH'
                                  : status === 'BARRIER_DOWN'
                                    ? 'OPEN'
                                    : '---'}
                          </div>

                          {/* Session origin indicator */}
                          {session && (
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                              {session.createdByStaff ? '🏢 Staff' : '👤 User'}
                            </div>
                          )}

                          {/* Duration + Fee */}
                          {session ? (() => {
                            const { finalFee } = getSessionFee(session);
                            return (
                              <>
                                <div style={{ fontSize: 14, color: '#666' }}>
                                  {formatDuration(session.startedAt || session.reservedAt)}
                                </div>
                                <div style={{
                                  fontSize: 16,
                                  fontWeight: 'bold',
                                  color: session.billingType === 'FREE' ? '#52c41a' : '#1890ff',
                                }}>
                                  {session.billingType === 'FREE' ? 'FREE' : formatFee(finalFee)}
                                </div>
                              </>
                            );
                          })() : (
                            <div style={{ fontSize: 14, color: '#666' }}>--</div>
                          )}

                          {/* Status indicators */}
                          <div style={{ marginTop: 6 }}>
                            {carPresent && <Tag color="red" style={{ fontSize: 14 }}>CAR</Tag>}
                            {telemetry?.hasFault && <Tag color="red" style={{ fontSize: 14 }}>ERR</Tag>}
                            {status === 'UNAUTHORIZED' && <Tag color="purple" style={{ fontSize: 14 }}>!</Tag>}
                          </div>

                          {/* Resetting Barrier indicator */}
                          {resettingSlots[slot.id] && (
                            <div style={{ fontSize: 11, fontWeight: 'bold', color: '#faad14', marginTop: 4 }}>
                              Resetting Barrier...
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))}
          </Card>
        );
      })}

      {/* Create Session Modal */}
      <Modal
        title={`Create Session - ${selectedSlot?.parkingName ? `${selectedSlot.parkingName} > ` : ''}${selectedSlot?.name || ''}`}
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          setSelectedSlot(null);
          setAssignedUser(null);
          setUserSearchValue('');
        }}
        footer={null}
        width={420}
      >
        <Form form={createForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="sessionType"
                label="Session Type"
                rules={[{ required: true }]}
              >
                <Select>
                  {SESSION_TYPES.map((type) => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="billingType"
                label="Billing"
                rules={[{ required: true }]}
              >
                <Select>
                  {BILLING_TYPES.map((type) => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.billingType !== curr.billingType}
          >
            {({ getFieldValue, setFieldsValue }) => {
              const billingType = getFieldValue('billingType');
              // Only show discount for non-FREE billing types
              if (billingType === 'FREE') return null;
              return (
                <Row gutter={12} align="middle">
                  <Col span={10}>
                    <Form.Item label=" " style={{ marginBottom: 0 }}>
                      <Button
                        block
                        icon={<GiftOutlined />}
                        onClick={() => setFieldsValue({ discountPercent: 100 })}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                      >
                        Free Session
                      </Button>
                    </Form.Item>
                  </Col>
                  <Col span={14}>
                    <Form.Item
                      name="discountPercent"
                      label="Discount %"
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        placeholder="0"
                        style={{ width: '100%' }}
                        addonAfter="%"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          <Form.Item name="plateNumber" label="Plate Number (optional)" style={{ marginTop: 16 }}>
            <Input placeholder="e.g., ABC-1234" size="large" />
          </Form.Item>

          <Form.Item name="notes" label="Notes (optional)">
            <TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>

          {/* Optional: Assign to User */}
          <Card
            size="small"
            style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
            title={
              <Space>
                <UserOutlined />
                <span>Assign to User (optional)</span>
              </Space>
            }
          >
            {assignedUser ? (
              <div>
                <Space style={{ marginBottom: 8 }}>
                  <Tag color="blue" icon={<UserOutlined />}>
                    {assignedUser.email}
                  </Tag>
                  <Button size="small" type="link" danger onClick={handleClearUser}>
                    Clear
                  </Button>
                </Space>
                <div style={{ color: '#666', fontSize: 12 }}>
                  {assignedUser.firstName && `${assignedUser.firstName} ${assignedUser.lastName || ''}`}
                  {assignedUser.phone && ` • ${assignedUser.phone}`}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  This user will see the session in their mobile app.
                </Text>
              </div>
            ) : (
              <div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="Search by phone or email"
                    value={userSearchValue}
                    onChange={(e) => setUserSearchValue(e.target.value)}
                    onPressEnter={handleUserSearch}
                    prefix={<SearchOutlined />}
                  />
                  <Button
                    type="primary"
                    onClick={handleUserSearch}
                    loading={searchingUser}
                    disabled={!userSearchValue.trim()}
                  >
                    Search
                  </Button>
                </Space.Compact>

                {/* Search Results */}
                {searchUserData?.searchUserByPhoneOrEmail && (
                  <div style={{ marginTop: 8, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Found user:
                    </Text>
                    <div>
                      <Text strong>{searchUserData.searchUserByPhoneOrEmail.email}</Text>
                      {searchUserData.searchUserByPhoneOrEmail.firstName && (
                        <div style={{ color: '#666' }}>
                          {searchUserData.searchUserByPhoneOrEmail.firstName} {searchUserData.searchUserByPhoneOrEmail.lastName || ''}
                        </div>
                      )}
                      {searchUserData.searchUserByPhoneOrEmail.phone && (
                        <div style={{ color: '#666' }}>{searchUserData.searchUserByPhoneOrEmail.phone}</div>
                      )}
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={() => handleSelectUser(searchUserData.searchUserByPhoneOrEmail)}
                    >
                      Select User
                    </Button>
                  </div>
                )}

                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  Leave empty for guest session (no app user).
                </Text>
              </div>
            )}
          </Card>

          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Primary action: Create & Lower Barrier */}
            {selectedSlot?.deviceId && (
              <Button
                type="primary"
                size="large"
                block
                icon={<UnlockOutlined />}
                loading={creating || sendingCommand}
                onClick={() => {
                  createForm.validateFields().then(values => {
                    handleCreateSession(values, true);
                  });
                }}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Start Session (Barrier goes DOWN)
              </Button>
            )}

            {/* Secondary: Reserve only (barrier stays up) */}
            <Button
              size="large"
              block
              icon={<LockOutlined />}
              loading={creating}
              onClick={() => {
                createForm.validateFields().then(values => {
                  handleCreateSession(values, false);
                });
              }}
              style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
            >
              Reserve Space (Barrier stays UP)
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Session Details Modal */}
      <Modal
        title={`Session - ${selectedSlot?.parkingName ? `${selectedSlot.parkingName} > ` : ''}${selectedSlot?.name || ''}`}
        open={isSessionDetailsVisible}
        onCancel={() => {
          setIsSessionDetailsVisible(false);
          setSelectedSession(null);
          setSelectedSlot(null);
        }}
        footer={null}
        width={480}
      >
        {selectedSession && selectedSlot && (
          <>
            {/* Session Info - Compact */}
            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>Status</Text>
                <div>
                  <Tag color={selectedSession.status === 'ACTIVE' ? 'green' : 'blue'} style={{ marginRight: 2 }}>
                    {selectedSession.status}
                  </Tag>
                  {isCarPresent(selectedSession) && <Tag color="red">CAR</Tag>}
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>Type / Billing</Text>
                <div>
                  <Tag style={{ marginRight: 2 }}>{selectedSession.sessionType}</Tag>
                  <Tag color={selectedSession.billingType === 'FREE' ? 'green' : selectedSession.billingType === 'MANUAL' ? 'orange' : 'default'}>
                    {selectedSession.billingType}
                  </Tag>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>Duration</Text>
                <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                  {formatDuration(selectedSession.startedAt || selectedSession.reservedAt)}
                </div>
              </Col>
            </Row>

            {/* User & Vehicle - Compact */}
            <Row gutter={8} style={{ marginBottom: 8, padding: '6px 8px', backgroundColor: '#fafafa', borderRadius: 4 }}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>User</Text>
                <div style={{ fontSize: 12 }}>
                  <strong>{selectedSession.user?.email || 'Guest'}</strong>
                  {selectedSession.user?.phone && <span style={{ color: '#888' }}> • {selectedSession.user.phone}</span>}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Plate / Created</Text>
                <div style={{ fontSize: 12 }}>
                  {(selectedSession.plateNumber || selectedSession.vehicle?.plateNumber) ? (
                    <Tag color="blue" style={{ marginRight: 4 }}>{selectedSession.plateNumber || selectedSession.vehicle?.plateNumber}</Tag>
                  ) : <span style={{ color: '#999' }}>-</span>}
                  {selectedSession.createdByStaff ? (
                    <Tag color="orange" style={{ fontSize: 10 }}>Staff</Tag>
                  ) : (
                    <Tag color="green" style={{ fontSize: 10 }}>App</Tag>
                  )}
                </div>
              </Col>
            </Row>

            {/* Barrier Controls - Centered */}
            {selectedSlot.deviceId && (
              <div style={{ marginBottom: 8, padding: '8px', backgroundColor: '#f0f5ff', borderRadius: 4, textAlign: 'center' }}>
                {!isCarPresent(selectedSession) ? (
                  <Space>
                    <Button
                      type="primary"
                      icon={<UnlockOutlined />}
                      onClick={() => handleBarrierCommand(selectedSlot.deviceId, 'down', selectedSlot.name)}
                      loading={sendingCommand}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Lower Barrier
                    </Button>
                    <Button
                      icon={<LockOutlined />}
                      onClick={() => handleBarrierCommand(selectedSlot.deviceId, 'up', selectedSlot.name)}
                      loading={sendingCommand}
                    >
                      Raise Barrier
                    </Button>
                  </Space>
                ) : (
                  <Text type="warning">
                    <CarOutlined /> Car on slot - barrier controls disabled
                  </Text>
                )}
              </div>
            )}

            {/* Edit session info - Compact */}
            <Form form={editForm} layout="inline" onFinish={handleUpdateSession} style={{ marginBottom: 8 }}>
              <Form.Item name="plateNumber" style={{ flex: 1, marginRight: 8 }}>
                <Input placeholder="Plate number" size="small" />
              </Form.Item>
              <Form.Item name="notes" style={{ flex: 2, marginRight: 8 }}>
                <Input placeholder="Notes" size="small" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" size="small" icon={<EditOutlined />}>
                  Update
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: '8px 0' }} />

            {/* Session Actions - Compact two rows */}
            {(() => {
              const telemetry = telemetryBySlotId[selectedSlot.id];
              const vehicleDetected = telemetry?.vehicleDetected || false;
              const canCancel = ['RESERVED', 'APPROACHING'].includes(selectedSession.status);
              const carOnSlot = isCarPresent(selectedSession) || vehicleDetected;
              // Get base fee (before discount) for computing fixed-cost discount percentage
              const { calculatedFee } = getSessionFee({
                ...selectedSession,
                discountPercent: 0  // Get base fee without discount
              });

              return (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {/* Row 1: Discount options */}
                  <Space wrap size="small" style={{ justifyContent: 'center', width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Discount:</Text>
                    {(selectedSession.discountPercent || 0) > 0 && (
                      <Tag color="green">{selectedSession.discountPercent}%</Tag>
                    )}
                    <Popconfirm title="Mark as FREE?" onConfirm={() => handleUpdateDiscount(100)}>
                      <Button size="small" icon={<GiftOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>
                        Free
                      </Button>
                    </Popconfirm>
                    <Popconfirm title="Apply 25%?" onConfirm={() => handleUpdateDiscount(25)}>
                      <Button size="small">25%</Button>
                    </Popconfirm>
                    <Popconfirm title="Apply 50%?" onConfirm={() => handleUpdateDiscount(50)}>
                      <Button size="small">50%</Button>
                    </Popconfirm>
                    <Space.Compact size="small">
                      <InputNumber
                        min={0}
                        placeholder="Cost"
                        style={{ width: 70 }}
                        prefix={DEFAULT_CURRENCY}
                        value={fixedCost}
                        onChange={(val) => setFixedCost(val)}
                      />
                      <Popconfirm
                        title={`Charge exactly ${formatCurrency(fixedCost || 0)}?`}
                        description={calculatedFee > 0 && fixedCost !== null ? `(${Math.round(((calculatedFee - fixedCost) / calculatedFee) * 100)}% discount from ${formatCurrency(calculatedFee)})` : undefined}
                        onConfirm={() => {
                          if (fixedCost !== null && fixedCost >= 0 && calculatedFee > 0) {
                            const discountAmount = calculatedFee - fixedCost;
                            const percent = Math.min(100, Math.max(0, Math.round((discountAmount / calculatedFee) * 100)));
                            handleUpdateDiscount(percent, `Fixed cost ${formatCurrency(fixedCost)}`);
                            setFixedCost(null);
                          }
                        }}
                        disabled={fixedCost === null || fixedCost < 0 || calculatedFee <= 0}
                      >
                        <Button
                          type="primary"
                          size="small"
                          disabled={fixedCost === null || fixedCost < 0 || calculatedFee <= 0}
                        >
                          Set
                        </Button>
                      </Popconfirm>
                    </Space.Compact>
                  </Space>

                  {/* Row 2: Action buttons - Bigger and centered */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                    {/* End session & Raise - only if device exists and no car */}
                    {selectedSlot.deviceId && !carOnSlot && (
                      <Popconfirm
                        title="End session and raise barrier?"
                        onConfirm={() => handleEndSessionAndRaise(true)}
                      >
                        <Button type="primary" icon={<StopOutlined />} loading={ending}>
                          END SESSION
                        </Button>
                      </Popconfirm>
                    )}

                    {/* End session without raise - when car detected or no device */}
                    {(carOnSlot || !selectedSlot.deviceId) && (
                      <Popconfirm
                        title={carOnSlot ? "⚠️ Vehicle detected!" : "End session?"}
                        description={carOnSlot ? "Car may still be parked." : "Billing will be processed."}
                        onConfirm={() => handleEndSessionAndRaise(false)}
                        okText={carOnSlot ? "End Anyway" : "OK"}
                        okButtonProps={carOnSlot ? { danger: true } : undefined}
                      >
                        <Button type="primary" icon={<StopOutlined />} loading={ending}>
                          End Session
                        </Button>
                      </Popconfirm>
                    )}

                    {/* Cancel - only for RESERVED/APPROACHING without vehicle */}
                    {canCancel && !vehicleDetected ? (
                      <Popconfirm
                        title="Cancel session?"
                        description="No charges applied."
                        onConfirm={handleCancelSession}
                      >
                        <Button danger icon={<CloseCircleOutlined />}>Cancel Reservation</Button>
                      </Popconfirm>
                    ) : (
                      <Tooltip title={vehicleDetected ? "Vehicle detected" : "Car is parked"}>
                        <Button danger icon={<CloseCircleOutlined />} disabled>Cancel</Button>
                      </Tooltip>
                    )}

                    {/* Close button */}
                    <Button
                      onClick={() => {
                        setIsSessionDetailsVisible(false);
                        setSelectedSession(null);
                        setSelectedSlot(null);
                        setFixedCost(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </Space>
              );
            })()}
          </>
        )}
      </Modal>

      {/* End Session - Payment Collection Modal */}
      <Modal
        title="Payment Collection"
        open={isEndSessionModalVisible}
        onCancel={() => {
          // Check if session still exists (hasn't auto-ended)
          const sessionStillActive = selectedSlot && sessionsBySlotId[selectedSlot.id];
          setIsEndSessionModalVisible(false);
          setPendingEndSession(null);
          if (sessionStillActive) {
            // Go back to session details if session still active
            setIsSessionDetailsVisible(true);
          } else {
            // Session already ended, clean up state
            setSelectedSession(null);
            setSelectedSlot(null);
          }
        }}
        footer={null}
        width={450}
      >
        {selectedSession && selectedSlot && (() => {
          const { calculatedFee, finalFee, durationMinutes } = getSessionFee(selectedSession);
          const hasDiscount = (selectedSession.discountPercent || 0) > 0;
          const isManual = selectedSession.billingType === 'MANUAL';
          const sessionAutoEnded = !sessionsBySlotId[selectedSlot.id];

          return (
            <>
              {/* Alert explaining why we're showing this */}
              <Alert
                type={sessionAutoEnded ? 'success' : (isManual ? 'warning' : 'info')}
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  sessionAutoEnded
                    ? 'Session Completed - Confirm Payment'
                    : isManual
                      ? 'Manual Billing - Staff must collect payment'
                      : 'No User Assigned - Cannot auto-charge'
                }
                description={
                  sessionAutoEnded
                    ? 'The customer has left and the session has ended. Please confirm if payment was collected.'
                    : isManual
                      ? 'This session was created with MANUAL billing. Please collect payment from the customer.'
                      : 'This session has no app user linked, so payment cannot be processed automatically.'
                }
              />

              {/* Session Info Summary */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary">Slot</Text>
                    <div style={{ fontWeight: 'bold' }}>{selectedSlot.name}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Duration</Text>
                    <div style={{ fontWeight: 'bold' }}>
                      {Math.floor(durationMinutes / 60)}h {durationMinutes % 60}m
                    </div>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Text type="secondary">Plate</Text>
                    <div>{selectedSession.plateNumber || selectedSession.vehicle?.plateNumber || '-'}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Customer</Text>
                    <div>{selectedSession.user?.email || selectedSession.user?.phone || 'Walk-in'}</div>
                  </Col>
                </Row>
              </Card>

              {/* Fee Breakdown */}
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  backgroundColor: '#f6ffed',
                  border: '1px solid #b7eb8f'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 14 }}>AMOUNT TO COLLECT</Text>
                  <div style={{
                    fontSize: 36,
                    fontWeight: 'bold',
                    color: '#52c41a',
                    marginTop: 4
                  }}>
                    {formatFee(finalFee)}
                  </div>
                  {hasDiscount && (
                    <div style={{ marginTop: 4 }}>
                      <Text delete type="secondary">{formatFee(calculatedFee)}</Text>
                      <Tag color="cyan" style={{ marginLeft: 8 }}>
                        {selectedSession.discountPercent}% off
                      </Tag>
                    </div>
                  )}
                </div>
              </Card>

              {/* Action Buttons */}
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  onClick={() => executeEndSession(pendingEndSession?.raiseBarrier || false, 'collected')}
                  loading={ending}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  {sessionAutoEnded ? 'Payment Was Collected' : 'Payment Collected - End Session'}
                </Button>

                <Button
                  size="large"
                  block
                  danger
                  onClick={() => executeEndSession(pendingEndSession?.raiseBarrier || false, 'unpaid')}
                  loading={ending}
                >
                  {sessionAutoEnded ? 'Payment Not Collected' : 'End Without Payment (Mark Unpaid)'}
                </Button>

                <Button
                  size="large"
                  block
                  onClick={() => {
                    // Check if session still exists
                    const stillActive = sessionsBySlotId[selectedSlot.id];
                    setIsEndSessionModalVisible(false);
                    setPendingEndSession(null);
                    if (stillActive) {
                      setIsSessionDetailsVisible(true);
                    } else {
                      setSelectedSession(null);
                      setSelectedSlot(null);
                    }
                  }}
                >
                  {sessionAutoEnded ? 'Close' : 'Cancel'}
                </Button>
              </Space>
            </>
          );
        })()}
      </Modal>

      {/* FAULT Slot Modal */}
      <Modal
        title={
          <Space>
            <StopOutlined style={{ color: '#ff4d4f' }} />
            <span>Barrier Fault - {selectedSlot?.name}</span>
          </Space>
        }
        open={isFaultModalVisible}
        onCancel={() => {
          setIsFaultModalVisible(false);
          setSelectedSlot(null);
          setFaultModalStep('initial');
        }}
        footer={null}
        width={480}
      >
        {selectedSlot && (() => {
          const telemetry = telemetryBySlotId[selectedSlot.id];

          // SUCCESS STEP
          if (faultModalStep === 'success') {
            return (
              <>
                <Alert
                  type="info"
                  showIcon
                  icon={<ClockCircleOutlined />}
                  message="Reset Command Sent"
                  description="Wait 30-60 seconds while the barrier resets. The slot will update automatically."
                  style={{ marginBottom: 16 }}
                />
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() => {
                    setIsFaultModalVisible(false);
                    setSelectedSlot(null);
                    setFaultModalStep('initial');
                  }}
                >
                  OK
                </Button>
              </>
            );
          }

          // CONFIRM STEP
          if (faultModalStep === 'confirm') {
            return (
              <>
                <Alert
                  type="warning"
                  showIcon
                  message="Safety Check Required"
                  description="Please, be SURE that the spot is empty, no vehicle parked, and the barrier is unobstructed."
                  style={{ marginBottom: 16 }}
                />
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Button
                    type="primary"
                    danger
                    size="large"
                    block
                    icon={<ReloadOutlined />}
                    loading={sendingCommand}
                    onClick={() => handleResetBarrier(selectedSlot)}
                  >
                    Barrier is unobstructed, reset the barrier
                  </Button>
                  <Button size="large" block onClick={() => setFaultModalStep('initial')}>
                    Go Back
                  </Button>
                </Space>
              </>
            );
          }

          // INITIAL STEP
          return (
            <>
              {telemetry?.faults?.length > 0 && (
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fff1f0', borderColor: '#ffa39e' }}>
                  <Text type="secondary">Detected Faults:</Text>
                  <div style={{ marginTop: 4 }}>
                    {telemetry.faults.map((fault: string, idx: number) => (
                      <Tag key={idx} color="red">{fault}</Tag>
                    ))}
                  </div>
                </Card>
              )}

              <Alert
                type="warning"
                showIcon
                message="Barrier may need attention"
                description="Oh, something can be wrong! Please, check visually the barrier condition. If barrier is ok, without obstructions, and the spot is completely free (no vehicle or other things) we can try to restart it."
                style={{ marginBottom: 16 }}
              />

              <Button
                type="primary"
                size="large"
                block
                icon={<ReloadOutlined />}
                onClick={() => setFaultModalStep('confirm')}
                style={{ marginBottom: 16 }}
                disabled={!selectedSlot.deviceId}
              >
                RESET THE BARRIER?
              </Button>

              {!selectedSlot.deviceId && (
                <Alert
                  type="error"
                  message="No device ID"
                  description="This slot has no associated device. Cannot send reset command."
                  style={{ marginBottom: 16 }}
                />
              )}

              <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4, fontSize: 13, color: '#666' }}>
                If resetting the barrier does not work, please contact Shades Parking's Support team, and share as many details you can.
              </div>

              <Button
                style={{ marginTop: 16 }}
                block
                onClick={() => {
                  setIsFaultModalVisible(false);
                  setSelectedSlot(null);
                }}
              >
                Close
              </Button>
            </>
          );
        })()}
      </Modal>

      {/* Pending Payments Drawer */}
      <Drawer
        title={
          <Space>
            <DollarOutlined />
            <span>Pending Payments</span>
            {pendingPaymentSessions.length > 0 && (
              <Badge count={pendingPaymentSessions.length} style={{ backgroundColor: '#fa8c16' }} />
            )}
          </Space>
        }
        placement="right"
        width={450}
        open={isPendingPaymentsVisible}
        onClose={() => setIsPendingPaymentsVisible(false)}
      >
        {pendingPaymentsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
            <div style={{ marginTop: 16 }}>Loading pending payments...</div>
          </div>
        ) : pendingPaymentSessions.length === 0 ? (
          <Alert
            type="success"
            message="All Clear!"
            description="No pending payments requiring attention."
            showIcon
          />
        ) : (
          <List
            dataSource={pendingPaymentSessions}
            renderItem={(session: any) => {
              const slotName = session.location?.name || 'Unknown Slot';
              const sectorName = session.location?.parent?.name || '';
              const parkingName = session.location?.parent?.parent?.name || '';
              const endedAt = session.endedAt ? new Date(session.endedAt) : null;
              const minutesAgo = endedAt ? Math.floor((Date.now() - endedAt.getTime()) / 60000) : 0;

              return (
                <List.Item
                  style={{
                    background: '#fffbe6',
                    marginBottom: 8,
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid #ffe58f'
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <Row justify="space-between" align="top">
                      <Col>
                        <div style={{ fontWeight: 'bold', fontSize: 16 }}>{slotName}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {parkingName} {sectorName && `› ${sectorName}`}
                        </Text>
                      </Col>
                      <Col>
                        <Tag color="orange">
                          {session.billingType === 'MANUAL' ? 'MANUAL' : 'NO USER'}
                        </Tag>
                      </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 8 }}>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Duration</Text>
                        <div>{session.durationMinutes ? `${Math.floor(session.durationMinutes / 60)}h ${session.durationMinutes % 60}m` : '-'}</div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Amount</Text>
                        <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                          {formatFee(session.finalFee || session.calculatedFee || 0)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Ended</Text>
                        <div>{minutesAgo < 60 ? `${minutesAgo}m ago` : `${Math.floor(minutesAgo / 60)}h ago`}</div>
                      </Col>
                    </Row>

                    {session.plateNumber && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Plate: </Text>
                        <Text>{session.plateNumber}</Text>
                      </div>
                    )}

                    <Space style={{ marginTop: 12, width: '100%' }} size="small">
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleConfirmPayment(session.id)}
                        loading={confirmingPayment}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Collected
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() => handleMarkUnpaid(session.id)}
                        loading={markingUnpaid}
                      >
                        Not Collected
                      </Button>
                    </Space>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Drawer>
    </div>
  );
};
